import { Link } from "react-router-dom";
import { useEffect, useState } from "react";
import { api } from "@/utils/api.js";
import { User } from "@/types/userTypes.js";
import { useSelector } from "react-redux";
import { useTranslation } from "react-i18next";
import { toast } from "@/utils/appToast";
import VerifySubmission from "@/shared/popups/verify";

const CertificationsTable = ({ refetchDependency, refetchCertifications }) => {
  const { t } = useTranslation();
  const [certifications, setCertifications] = useState(null);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [certificationToDelete, setCertificationToDelete] = useState<string | null>(null);
  const [isDeleting, setIsDeleting] = useState(false);
  const { user: me }: { user: User } = useSelector(
    (state: { user }) => state.user
  );

  useEffect(() => {
    // Only fetch if user exists and is a worker
    if (!me || me.type !== 'worker') {
      return;
    }
    
    // Handle both cases: workerProfile as string ID or as object
    const workerProfileId = typeof me?.workerProfile === 'string' 
      ? me.workerProfile 
      : (me?.workerProfile?._id || me?.workerProfile?.id);
    
    if (workerProfileId) {
      fetchCertifications();
    }
  }, [refetchDependency, me?.workerProfile, me?.type]);

  const fetchCertifications = async () => {
    try {
      // Handle both cases: workerProfile as string ID or as object
      const workerProfileId = typeof me?.workerProfile === 'string' 
        ? me.workerProfile 
        : (me?.workerProfile?._id || me?.workerProfile?.id);
      
      if (!workerProfileId) {
        console.warn("Worker profile ID not available");
        return;
      }
      
      const res = await api.getCertificationsByWorkerId(workerProfileId);
      if(res && res?.success){
        setCertifications(res.data);
      }
    } catch (error: any) {
      // Don't let API errors cause logout - just log and show empty state
      console.error("Failed to fetch certifications", error);
      // Set empty array instead of null to show empty state
      setCertifications([]);
    }
  };

  const deleteCertificationHandler = async (certificationId: string) => {
    setIsDeleting(true);
    try {
      await api.deleteCertificationById(certificationId);
      toast.success(t('certificationDeleted'));
      refetchCertifications();
      setShowDeleteConfirm(false);
      setCertificationToDelete(null);
    } finally {
      setIsDeleting(false);
    }
  };

  const openDeleteConfirm = (item: { _id: string }) => {
    setCertificationToDelete(item._id);
    setShowDeleteConfirm(true);
  };

  const handleConfirmDelete = () => {
    if (certificationToDelete) {
      deleteCertificationHandler(certificationToDelete);
    }
  };

  return (
    <div className="tabs-box">
      <div className="widget-title">
        <h4>{t('myCertifications')}</h4>
      </div>
      {/* End filter top bar */}

      {/* Start table widget content */}
      <div className="widget-content">
        <div className="table-outer">
          <div className="table-outer">
            <table className="default-table manage-job-table">
              <thead>
                <tr>
                  <th>{t('document')}</th>
                  <th>{t('actions')}</th>
                </tr>
              </thead>

              <tbody>
                {certifications &&
                  certifications.map((item) => (
                    <tr key={item._id || item.id}>
                      <td>
                        {/* <!-- Job Block --> */}
                        <div className="job-block">
                          <div className="inner-box" style={{}}>
                            <h4 style={{ margin: 0 }}>
                              <Link to={item.url} target="_blank">
                                {item.name}
                              </Link>
                            </h4>
                          </div>
                        </div>
                      </td>
                      <td>
                        <div className="option-box">
                          <ul className="option-list">
                            <li>
                              <Link
                                to={item.url}
                                target="_blank"
                                data-text={t('view_details')}
                              >
                                <span className="la la-eye"></span>
                              </Link>
                            </li>
                            <li>
                              <button
                                data-text={t('clear')}
                                onClick={() => openDeleteConfirm(item)}
                              >
                                <span className="la la-trash"></span>
                              </button>
                            </li>
                          </ul>
                        </div>
                      </td>
                    </tr>
                  ))}
              </tbody>
            </table>
          </div>
        </div>
      </div>
      {/* End table widget content */}

      <VerifySubmission
        isOpen={showDeleteConfirm}
        handleClose={() => {
          setShowDeleteConfirm(false);
          setCertificationToDelete(null);
        }}
        handleSubmit={handleConfirmDelete}
        isLoading={isDeleting}
        size="xs"
        headerTitle={t('deleteCertificationConfirmTitle')}
        cancelButtonLabel={t('cancel')}
        confirmButtonLabel={t('yes')}
      >
        <p style={{ fontSize: '15px', color: '#333', lineHeight: 1.5 }}>
          {t('deleteCertificationConfirmMessage')}
        </p>
      </VerifySubmission>
    </div>
  );
};

export default CertificationsTable;
